import { renderPreview } from './template-variables';

interface Props {
  subject: string;
  body: string;
}

// Tags and attributes produced by the RichTextEditor. Anything outside this set is stripped.
const ALLOWED_TAGS = new Set(['p', 'strong', 'em', 'u', 'ul', 'ol', 'li', 'a', 'br']);
const ALLOWED_LINK_SCHEMES = /^(https?:|mailto:)/i;

function sanitizeNode(source: ChildNode, target: Node): void {
  for (const child of Array.from(source.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      target.appendChild(child.cloneNode());
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const el = child as Element;
      const tag = el.tagName.toLowerCase();
      if (ALLOWED_TAGS.has(tag)) {
        const safe = document.createElement(tag);
        if (tag === 'a') {
          const href = el.getAttribute('href') ?? '';
          if (ALLOWED_LINK_SCHEMES.test(href)) {
            safe.setAttribute('href', href);
          }
        }
        sanitizeNode(el, safe);
        target.appendChild(safe);
      } else {
        // Strip the tag but keep its text content
        sanitizeNode(el, target);
      }
    }
  }
}

function sanitizeHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const container = document.createElement('div');
  sanitizeNode(doc.body, container);
  return container.innerHTML;
}

export function EmailPreview({ subject, body }: Props): React.ReactNode {
  const renderedSubject = renderPreview(subject);
  const renderedBody = sanitizeHtml(renderPreview(body));

  return (
    <div className="flex flex-col gap-3">
      {/* Email client mockup */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xs">
        {/* Email header bar */}
        <div className="flex items-center gap-1.5 border-b border-slate-100 bg-slate-50 px-3 py-2">
          <div className="size-2.5 rounded-full bg-red-400" />
          <div className="size-2.5 rounded-full bg-yellow-400" />
          <div className="size-2.5 rounded-full bg-green-400" />
        </div>
        {/* Email content */}
        <div className="px-5 py-4">
          <div className="mb-3 border-b border-slate-100 pb-3">
            <p className="text-xs text-muted-foreground">From: noreply@kiku.ai</p>
            <p className="text-xs text-muted-foreground">To: john.doe@example.com</p>
            <p className="mt-1.5 text-sm font-semibold text-foreground">{renderedSubject}</p>
          </div>
          <div
            className="text-xs leading-relaxed text-slate-700 [&_a]:text-primary [&_a]:underline [&_ol]:mb-2 [&_ol]:list-decimal [&_ol]:pl-4 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:mb-2 [&_ul]:list-disc [&_ul]:pl-4"
            dangerouslySetInnerHTML={{ __html: renderedBody }}
          />
        </div>
      </div>
    </div>
  );
}
